/**
 * Links Normalization Utility
 * 
 * Purpose:
 * Provides string normalization functions for matching category names and link identifiers.
 * Handles whitespace/newline mismatches between API category names and expected category names
 * by collapsing all whitespace to single spaces and lowercasing for comparison. Original names
 * are preserved for display purposes.
 * 
 * High-level Workflow:
 * 1. Input: String (category name, link name, etc.)
 * 2. Validation: Check if input is valid string (return empty string if not)
 * 3. Normalization: Trim, collapse all whitespace (spaces, newlines, tabs) to single space, lowercase
 * 4. Output: Normalized string for matching/comparison
 * 
 * Assumptions & Invariants:
 * - Input may be null, undefined, or non-string (handled gracefully with empty string return)
 * - Whitespace variations are normalized: "CLC\nContract" and "CLC  Contract" both become "clc contract"
 * - Original case is lost (lowercase output) - only used for matching, not display
 * - Empty string input returns empty string (no error thrown)
 * 
 * Dependencies:
 * - External:
 *   - None (pure JavaScript string operations)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Phase 1 extraction - Extracted normalizeNameForMatching() from OwnerLinksReportsPage.jsx.
 *   Function moved verbatim with no logic changes. Used for category name matching and help file
 *   grouping heuristics.
 */

/**
 * Normalize a string for matching: trim, collapse whitespace (including newlines/tabs) to single spaces, lowercase
 * This handles cases where DB stores "CLC\nContract" or "CLC  Contract" but we expect "CLC Contract"
 * 
 * Intent: Pure utility function for category name matching. Handles whitespace/newline mismatches
 * between API category names and expected DROPBOX_CATEGORIES. Used in linksByCategory Map
 * construction and category lookup.
 * 
 * @param {string|null|undefined} name - String to normalize
 * @returns {string} - Normalized string (empty string if input is invalid)
 */
export function normalizeNameForMatching(name) {
  if (!name || typeof name !== 'string') return '';
  return name
    .trim()
    .replace(/\s+/g, ' ') // Collapse all whitespace (spaces, newlines, tabs) to single space
    .toLowerCase();
}
